import React from 'react'
import { parseQuery, transformKeys } from '../../util'
import _ from 'lodash'
import { responseErrors } from '../../fetchUtils'
import queries from './queries'
import ClassManagement from './ClassManagement'
import { faCircle, faAdjust } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const routeMap = {
  '/classes/':            'LIST',
  '/classes/:id':         'SHOW'
}
const routes = Object.keys(routeMap)

// TODO solve useMemo problem.
// This container introduced a form pattern where defaultValues are frozen (ie, they are prevented
// from updating even when their form function re-renders) using React's useMemo. This pattern was
// suggested in react-form's documentation at https://github.com/tannerlinsley/react-form/blob/master/docs/api.md#form-options
// in the `defaultValues` bullet.
// The defaultValue has to be frozen somehow due to problems re-rendering. Consider the following flow:
// - Course name is changed and form submitted
// - Submit fails due to 4xx or 5xx server response
// - The submission error gets stored in this container's state, which cascades down to the form function which re-renders
// - Due to the re-render, the defaultValue is reset to the unmodified course params which is annoying and not how forms are supposed to behave
// However, reading the useMemo docs mentions that useMemo should be an optimization and code should work without it
// (https://reactjs.org/docs/hooks-reference.html#usememo) since React cannot guarantee that memos will always be preserved
// in future React releases. Some alternative pattern needs to be figured out for preserving defaultValues in between re-renders.
class ClassManagementContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      latestPath: '',               // most recent match.path from React Router
      rosters: [],                  // set of courses with sections and students
      courseCollection: [],         // value-label pairs of courses for dropdowns
      section: {},                  // single section, set for the /classes/:id page
      googleAuthorizationLink: '',  // If present, the user should go to this link and authorize Classroom import. If blank, fetching either hasn't occured or the user's authorized.
      googleClassrooms: '',         // Google Classrooms that the user can import students from
      isGooglePopupOpen: false,     // Controls whether the import popup is showing or not on the section page
      wasGoogleImportSuccessful: false, // Shown after a successful import.

      // fetching and error states
      isResettingPasswords: false,
      passwordResetErrors: [],
      isImporting: false,
      importErrors: [],
      isClassroomFetching: false,
      classroomFetchErrors: [],
      isRosterFetching: false,
      rosterFetchErrors: [],
      isSectionFetching: false,
      sectionFetchErrors: [],
      isCreatingSection: false,
      createSectionErrors: [],
      isUpdatingCourse: false,
      updateCourseErrors: [],
      isUpdatingSection: false,
      updateSectionErrors: [],
      isUpsertingStudents: false,
      upsertStudentsErrors: [],
      isDeletingStudents: false,
      deleteStudentsErrors: [],
      isTransferringStudents: false,
      transferStudentsErrors: [],
      targetSectionId: '',           //When transfering students, this is the section id that the students have been transfered to.
      activityIndicator: {}         //object that holds levels of user activity with the game for student forms
    }
  }

  componentDidMount() { this.fetchByRoute() }
  componentDidUpdate() { this.fetchByRoute() }

  fetchByRoute = ()=> {
    const { match } = this.props
    const { latestPath } = this.state
    const currentAction = routeMap[match.path]

    const queryParams = parseQuery(this.props.location.search)
    if(!!queryParams.import != this.state.isGooglePopupOpen){ this.setState({ isGooglePopupOpen: !!queryParams.import }) }

    // If URL is unchanged, do not fetch
    if(match.path == latestPath) { return; }
    else{ this.setState({ latestPath: match.path }); }

    // Fetch based on currentAction
    switch(currentAction) {
      case 'LIST':
        this.fetchAllRosters();
        break;
      case 'SHOW':
        this.fetchSection(match.params.id);
        break;
      default:
        //TODO
        console.log('default hit')
    }
  }

  fetchAllRosters = ()=> {
    this.setState({ isRosterFetching: true, rosterFetchErrors: [] })

    const callbacks = {
      onComplete: json =>{
        const errors = responseErrors(json)

        if(_.isEmpty(errors)) {
          this.setState({ rosters: json.data.courses, isRosterFetching: false })
        } else {
          this.setState({ rosterFetchErrors: errors, isRosterFetching: false });
        }
      },
      onFailure: error => { this.setState({ rosterFetchErrors: [error], isRosterFetching: false }); }
    }

    queries.fetchAllRosters(callbacks)
  }

  fetchSection = (sectionId, onSuccess, hidingLoading=false) => {
    this.setState({ isSectionFetching: !hidingLoading, sectionFetchErrors: [] })

    const callbacks = {
      onComplete: json =>{
        const errors = responseErrors(json)

        if(_.isEmpty(errors)) {
          this.updateStudentActivityIndicators(json.data.section.students)
          const col = _.map(json.data.courses, c => transformKeys(c, { "id": "value", "name": "label" }))
          this.setState({ courseCollection: col, section: json.data.section, isSectionFetching: false })
        } else {
          this.setState({ sectionFetchErrors: errors, isSectionFetching: false });
        }
      },
      onFailure: error => { this.setState({ sectionFetchErrors: [error], isSectionFetching: false }); }
    }

    queries.fetchSection(callbacks, { id: sectionId })
  }

  updateStudentActivityIndicators = (students) => {
    //initiate object
    //for each student
    //  assign an attribute to them based on if they have a char name, and if they have any quest progresses that are completed
    let indicatorObject = {}
    _.map(students, student => {
      let studentActivityLevel = <FontAwesomeIcon icon={["far","circle"]} color='#6d5cae'/>
      if (student.hasCompletedQuestProgress) {
        studentActivityLevel = <FontAwesomeIcon icon={faCircle} color='#6d5cae'/>
      } else if(!_.isEmpty(student.profile.characterName)) {
          studentActivityLevel = <FontAwesomeIcon icon={faAdjust} color='#6d5cae'/>
      }
      indicatorObject[student.profile.uuid.toString()] = studentActivityLevel
    })
    this.setState({ activityIndicator: indicatorObject})
  }


  handleEditCourse = formValues => {
    this.setState({ isUpdatingCourseSection: true })
    //TODO
  }

  handleSubmitNewSection = formValues => {
    this.setState({ isCreatingSection: true, createSectionErrors: [] })

    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)

        if(_.isEmpty(errors)) {
          // Redirect to new section on success
          const newSection = json['data']['createSection']['section']
          this.props.history.push(`/classes/${newSection.id}`)

        } else {
          this.setState({ createSectionErrors: errors, isCreatingSection: false });
        }
      },
      onFailure: ()=>{
        this.setState({ createSectionErrors: ['Sorry, something went wrong!'], isCreatingSection: false });
      }
    }
    queries.createSection(callbacks, formValues)
  }

  handleResetSectionPasswords = formValues => {
    this.setState({ isResettingPasswords: true, resetPasswordErrors: [] })

    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)
        if(_.isEmpty(errors)) {
          this.setState({ section: json['data']['resetStudentPasswords']['section'], isResettingPasswords: false })
        } else {
          this.setState({ resetPasswordErrors: errors, isResettingPasswords: false });
        }
      },
      onFailure: ()=>{
        this.setState({ isResettingPasswords: false, resetPasswordErrors: ['Sorry, something went wrong!'] })
      }
    }
    queries.resetPasswords(callbacks, formValues)
  }

  handleUpdateSection = formValues => {
    this.setState({ isUpdatingSection: true, updateSectionErrors: [] })
    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)

        if(_.isEmpty(errors)) {
          this.setState({ section: json['data']['updateSection']['section'], isUpdatingSection: false })
        } else {
          this.setState({ updateSectionErrors: errors, isUpdatingSection: false });
        }
      },
      onFailure: ()=>{
        this.setState({ updateSectionErrors: ['Sorry, something went wrong!'], isUpdatingSection: false });
      }
    }
    queries.updateSection(callbacks, formValues)
  }

  handleUpsertStudents = (formValues, onSuccess) => {
    this.setState({ isUpsertingStudents: true, upsertStudentsErrors: [] })

    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)

        if(_.isEmpty(errors)) {
          if(_.isFunction(onSuccess)){ onSuccess() }
          this.addNewActivityIndicator(json['data']['upsertStudents']['section']['students'])
          this.setState({ section: json['data']['upsertStudents']['section'], isUpsertingStudents: false })
        } else {
          this.setState({ upsertStudentsErrors: errors, isUpsertingStudents: false });
        }
      },
      onFailure: ()=>{
        this.setState({ upsertStudentsErrors: ['Sorry, something went wrong!'], isUpsertingStudents: false });
      }
    }
    queries.upsertStudents(callbacks, formValues)
  }

  addNewActivityIndicator = newStudents => {
    //adds a new activity indicator for each student not currently represented within the activity indicator
    const currentActivityIndicator = this.state.activityIndicator
    _.map(newStudents, s => {
      if(!_.includes(Object.keys(currentActivityIndicator), s.profile.uuid.toString())){
        currentActivityIndicator[s.profile.uuid.toString()] = <FontAwesomeIcon icon={["far","circle"]} color='#6d5cae'/>
      }
    })
    this.setState({ activityIndicator: currentActivityIndicator})
  }


  handleUpdateCourse = (formValues, onSuccess) => {
    this.setState({ isUpdatingCourse: true, updateCourseErrors: [] })

    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)

        if(_.isEmpty(errors)) {
          const course = json['data']['updateCourse']['course']
          let newRosters = Object.assign(this.state.rosters, [])
          const index = _.findIndex(newRosters, r => r.id === course.id)
          newRosters[index] = course

          if(_.isFunction(onSuccess)){ onSuccess() }
          this.setState({ rosters: newRosters, isUpdatingCourse: false });
        } else {
          this.setState({ updateCourseErrors: errors, isUpdatingCourse: false });
        }
      },
      onFailure: ()=>{
        this.setState({ createSectionErrors: ['Sorry, something went wrong!'], isCreatingSection: false });
      }
    }
    queries.updateCourse(callbacks, formValues)
  }

  handleDeleteStudents = (studentIds, onSuccess) => {
    this.setState({ isDeletingStudents: true, deleteStudentsErrors: [] })
    const vars = {
      studentIds: studentIds
    }
    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)
        if(_.isEmpty(errors)) {
          if(_.isFunction(onSuccess)){ onSuccess() }
          this.fetchSection(this.state.section.id, true)
          this.setState({ isDeletingStudents: false})
        } else {
          this.setState({ isDeletingStudents: false, deleteStudentsErrors: errors })
        }
      },
      onFailure: ()=>{
        this.setState({ deleteStudentsErrors: ['Sorry, something went wrong!'], isDeletingStudents: false })
      }
    }
    queries.deleteStudents(callbacks, vars)
  }

  handleTransferStudents = (vars, onSuccess) => {
    this.setState({ isTransferringStudents: true, transferStudentsErrors: [] })
    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)
        if(_.isEmpty(errors)){
          if(_.isFunction(onSuccess)){ onSuccess() }
          this.setState({ isTransferringStudents: false, targetSectionId: json['data']['transferStudents']['targetSectionId'] })
        } else {
          this.setState({ isTransferringStudents: false, transferStudentsErrors: errors })
        }
      },
      onFailure: ()=>{
        this.setState({ transferStudentsErrors: ['Sorry, something went wrong!'], isTransferringStuents: false })
      }
    }
    queries.transferStudents(callbacks, vars)
  }

  handleClearTransferErrors = () => { //made to clear errors so that re-opening the transfer popup will not show previous submission errors.
    this.setState({ transferStudentsErrors: []})
  }

  handleVisitTargetSection= () => {
    this.props.history.push(`/classes/${this.state.targetSectionId}`)
    this.fetchSection(this.state.targetSectionId)
  }

  fetchGoogleClassrooms = ()=> {
    this.setState({ isClassroomFetching: true, classroomFetchErrors: [] })

    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)

        if(_.isEmpty(errors)) {
          const { sections, authorizationLink } = json.data.googleSections
          this.setState({ isClassroomFetching: false, googleAuthorizationLink: authorizationLink, googleClassrooms: sections })
        } else {
          this.setState({ isClassroomFetching: false, classroomFetchErrors: errors })
        }
      },
      onFailure: e=>{
        this.setState({ isClassroomFetching: false, classroomFetchErrors: ['Sorry, something went wrong!'] })
      }
    }

    queries.fetchGoogleClassrooms(callbacks)
  }

  importClassroomStudents = (classroomId, sectionId) => {
    this.setState({ isImporting: true, importErrors: [] })

    const callbacks = {
      onComplete: json => {
        const errors = responseErrors(json)

        if(_.isEmpty(errors)) {
          const section = json.data.importGoogleClassroomStudents.section
          this.setState({ wasGoogleImportSuccessful: true, isImporting: false, importErrors: [], section })
          // Show successful import for a few seconds
          setTimeout(()=>this.setState({ wasGoogleImportSuccessful: false }), 2000)
        } else {
          this.setState({ isImporting: false, importErrors: errors })
        }
      },
      onFailure: e=>{
        this.setState({ isImporting: false, importErrors: ['Sorry, something went wrong!'] })
      }
    }

    queries.importGoogleClassroomStudents(callbacks, { classroomId, sectionId })
  }

  handleGoogleImportPopupClose = id => this.props.history.push(`/classes/${id}`)

  render() {
    const { match, currentUser } = this.props
    const { rosters, section, courseCollection } = this.state
    // Fetch-related states
    const { handleResetSectionPasswords, isResettingPasswords, passwordResetErrors, isUpsertingStudents, upsertStudentsErrors, handleUpsertStudents, wasGoogleImportSuccessful, isImporting, importErrors, isGooglePopupOpen, googleClassrooms, googleAuthorizationLink, isClassroomFetching, classroomFetchErrors, updateSectionErrors, isUpdatingSection, updateCourseErrors, isUpdatingCourse, rosterFetchErrors, isRosterFetching, sectionFetchErrors, isSectionFetching, createSectionErrors, isCreatingSection, isDeletingStudents, deleteStudentsErrors, isTransferringStudents, transferStudentsErrors, targetSectionId, activityIndicator } = this.state

    return(
      <ClassManagement
        currentUser={currentUser}
        rosters={rosters}
        section={section}
        activityIndicator={activityIndicator}
        courseCollection={courseCollection}
        currentAction={routeMap[match.path]}

        wasGoogleImportSuccessful={wasGoogleImportSuccessful}
        importClassroomStudents={this.importClassroomStudents}
        isImporting={isImporting}
        importErrors={importErrors}
        googleAuthorizationLink={googleAuthorizationLink}
        googleClassrooms={googleClassrooms}
        fetchGoogleClassrooms={this.fetchGoogleClassrooms}
        isClassroomFetching={isClassroomFetching}
        classroomFetchErrors={classroomFetchErrors}
        isGooglePopupOpen={isGooglePopupOpen}
        handleGoogleImportPopupClose={this.handleGoogleImportPopupClose}

        handleResetSectionPasswords={this.handleResetSectionPasswords}
        isResettingPasswords={isResettingPasswords}
        passwordResetErrors={passwordResetErrors}

        handleFetchRosters={this.fetchAllRosters}
        isRosterFetching={isRosterFetching}
        rosterFetchErrors={rosterFetchErrors}

        handleFetchSection={this.fetchSection}
        isSectionFetching={isSectionFetching}
        sectionFetchErrors={sectionFetchErrors}

        handleUpsertStudents={this.handleUpsertStudents}
        isUpsertingStudents={isUpsertingStudents}
        upsertStudentsErrors={upsertStudentsErrors}

        handleUpdateCourse={this.handleUpdateCourse}
        isUpdatingCourse={isUpdatingCourse}
        updateCourseErrors={updateCourseErrors}

        handleSubmitNewSection={this.handleSubmitNewSection}
        createSectionErrors={createSectionErrors}
        isCreatingSection={isCreatingSection}

        handleUpdateSection={this.handleUpdateSection}
        updateSectionErrors={updateSectionErrors}
        isUpdatingSection={isUpdatingSection}

        handleDeleteStudents={this.handleDeleteStudents}
        deleteStudentsErrors={deleteStudentsErrors}
        isDeletingStudents={isDeletingStudents}

        handleTransferStudents={this.handleTransferStudents}
        transferStudentsErrors={transferStudentsErrors}
        isTransferringStudents={isTransferringStudents}
        handleClearTransferErrors={this.handleClearTransferErrors}
        handleVisitTargetSection={this.handleVisitTargetSection}
      />
    );
  }
}

export { routes };
export default ClassManagementContainer
